import type { ApiKeyAudience } from '../../lib/api-client';

/**
 * Per-audience eligible scope lists — mirrors the server's AUDIENCE_ELIGIBLE_SCOPES.
 * Shared by MintKeyModal (create) and EditKeyModal (update) so a key only ever
 * offers scopes valid for its protocol.
 */
const ELIGIBLE_SCOPES: Record<ApiKeyAudience, readonly string[]> = {
  mcp: ['mcp:read', 'mcp:execute', 'mcp:write'],
  direct_api: [
    'conversations:read',
    'conversations:write',
    'sessions:execute',
    'profiles:read',
    'profiles:write',
  ],
  openai_completions: ['completions:execute'],
  integration: ['integration:provision', 'conversations:read', 'conversations:write'],
};

interface ScopePillsProps {
  /** The key's audience — determines which scopes are offered. */
  audience: ApiKeyAudience;
  selected: string[];
  onToggle: (scope: string) => void;
  disabled?: boolean;
}

/** Checkbox pill-toggles for a key's scopes, filtered to the audience's eligible list. */
export function ScopePills({ audience, selected, onToggle, disabled = false }: ScopePillsProps) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {ELIGIBLE_SCOPES[audience].map((s) => {
        const active = selected.includes(s);
        return (
          <label
            key={s}
            data-testid={`scope-${s}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text-soft)',
              fontSize: 12,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.55 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={active}
              disabled={disabled}
              onChange={() => onToggle(s)}
              style={{ display: 'none' }}
            />
            {s}
          </label>
        );
      })}
    </div>
  );
}
